import { collection, getDocs, getDoc, doc, setDoc, query, where, orderBy, documentId, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger.ts';

// ========================================
// Logger 설정 - DailyReportFirebaseService
// ========================================
const logger = createLogger('DailyReportFirebaseService');
const DEBUG = false;

// 빈 file 객체 제거 유틸리티 함수 (private)
const removeEmptyFileObjects = (data: any) => {
  const cleanData = { ...data };

  if (cleanData.file) {
    const fileName = cleanData.file.name || '';
    const isEmptyFile = !fileName || fileName.trim() === '' || fileName === 'undefined';

    if (isEmptyFile) {
      DEBUG && logger.info('🗑️ [File 최적화] 빈 file 객체 제거됨:', cleanData.file);
      delete cleanData.file;
    } else {
      DEBUG && logger.info('✅ [File 최적화] 유효한 file 객체 유지:', cleanData.file);
    }
  }

  return cleanData;
};

// 수납 데이터 저장하기
export const saveDailyReceiptData = async (receiptData: any, date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, { receipts: receiptData, updatedAt: Timestamp.now() }, { merge: true });
    return true;
  } catch (error) {
    DEBUG && logger.error('수납 데이터 저장 실패:', error);
    throw error;
  }
};

// 지출 데이터 저장하기
export const saveDailyExpenseData = async (expenseData: any, date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, { expenses: expenseData, updatedAt: Timestamp.now() }, { merge: true });
    return true;
  } catch (error) {
    DEBUG && logger.error('지출 데이터 저장 실패:', error);
    throw error;
  }
};

// 통합 거래 데이터 저장하기 (지출/수입)
export const saveFinancialTransactionData = async (financialData: any, date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, {
      financialTransactions: financialData,
      updatedAt: Timestamp.now()
    }, { merge: true });

    DEBUG && logger.info('[Firebase Service] 거래 데이터 저장 완료:', {
      date: date,
      totalExpense: financialData.totalExpense,
      totalIncome: financialData.totalIncome,
      netAmount: financialData.netAmount,
      itemsCount: financialData.items?.length || 0
    });

    return true;
  } catch (error) {
    DEBUG && logger.error('거래 데이터 저장 실패:', error);
    throw error;
  }
};

// 어시스트 수납 데이터 저장하기
export const saveAssistReceiptData = async (assistData: any, date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, { assistReceipts: assistData, updatedAt: Timestamp.now() }, { merge: true });
    return true;
  } catch (error) {
    DEBUG && logger.error('어시스트 수납 데이터 저장 실패:', error);
    throw error;
  }
};

// 내원경로 수납 데이터 저장하기
export const saveReferralReceiptData = async (referralData: any, date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, { referralReceipts: referralData, updatedAt: Timestamp.now() }, { merge: true });
    return true;
  } catch (error) {
    DEBUG && logger.error('내원경로 수납 데이터 저장 실패:', error);
    throw error;
  }
};

// assistReceipts 데이터 업데이트 (기존 데이터와 병합)
export const updateAssistReceipts = async (newItems: any[], date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    const docSnapshot = await getDoc(dailyReportRef);

    let updatedAssistReceipts = newItems;

    if (docSnapshot.exists()) {
      const existingData = docSnapshot.data();
      updatedAssistReceipts = [
        ...(existingData.assistReceipts || []),
        ...newItems
      ];
    }

    await setDoc(dailyReportRef, {
      assistReceipts: updatedAssistReceipts,
      updatedAt: Timestamp.now()
    }, { merge: true });

    DEBUG && logger.info('[Firebase Service] assistReceipts 업데이트 완료:', {
      date: date,
      newItemsCount: newItems.length,
      totalItemsCount: updatedAssistReceipts.length
    });

    return true;
  } catch (error) {
    DEBUG && logger.error('assistReceipts 업데이트 실패:', error);
    throw error;
  }
};

// referralReceipts 데이터 업데이트 (기존 데이터와 병합)
export const updateReferralReceipts = async (newItems: any[], date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    const docSnapshot = await getDoc(dailyReportRef);

    let updatedReferralReceipts = newItems;

    if (docSnapshot.exists()) {
      const existingData = docSnapshot.data();
      updatedReferralReceipts = [
        ...(existingData.referralReceipts || []),
        ...newItems
      ];
    }

    await setDoc(dailyReportRef, {
      referralReceipts: updatedReferralReceipts,
      updatedAt: Timestamp.now()
    }, { merge: true });

    DEBUG && logger.info('[Firebase Service] referralReceipts 업데이트 완료:', {
      date: date,
      newItemsCount: newItems.length,
      totalItemsCount: updatedReferralReceipts.length
    });

    return true;
  } catch (error) {
    DEBUG && logger.error('referralReceipts 업데이트 실패:', error);
    throw error;
  }
};

// 일일 보고서의 모든 데이터 저장하기
export const saveDailyReportData = async (reportData: any, date: string) => {
  try {
    const cleanedData = removeEmptyFileObjects(reportData);

    DEBUG && logger.info('[Firebase Service] 저장 시작:', {
      date: date,
      hasReceipts: !!cleanedData.receipts,
      receiptsCount: cleanedData.receipts?.items?.length || 0,
      hasAssistReceipts: !!cleanedData.assistReceipts,
      assistReceiptsCount: cleanedData.assistReceipts?.length || 0,
      hasAssistCashierDoctorReceipts: !!cleanedData.assistCashierDoctorReceipts,
      assistCashierDoctorReceiptsCount: cleanedData.assistCashierDoctorReceipts?.length || 0,
      assistReceiptsWithDoctor: cleanedData.assistReceipts?.filter((item: any) => item.doctorName).length || 0,
      assistReceiptsWithCashier: cleanedData.assistReceipts?.filter((item: any) => item.cashierName).length || 0,
      receiptsWithCashier: cleanedData.receipts?.items?.filter((item: any) => item.cashierName).length || 0,
      hasTeamCollection: !!cleanedData.teamCollection,
      teamCollectionCount: cleanedData.teamCollection?.length || 0,
      hasFileObject: !!cleanedData.file,
      originalHadFile: !!reportData.file
    });

    const dailyReportRef = doc(db, 'dailyReports', date);
    await setDoc(dailyReportRef, {
      ...cleanedData,
      updatedAt: Timestamp.now()
    }, { merge: true });

    DEBUG && logger.info('[Firebase Service] 저장 완료:', date);
    return true;
  } catch (error) {
    DEBUG && logger.error('일일 보고서 저장 실패:', error);
    throw error;
  }
};

// 특정 날짜의 일일 보고서 가져오기
export const getDailyReportData = async (date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    const docSnapshot = await getDoc(dailyReportRef);

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();

      // 하위 호환성: expenses와 financialTransactions 모두 지원
      if (data && !data.expenses && data.financialTransactions) {
        const expenseItems = data.financialTransactions.items
          ? data.financialTransactions.items.filter((item: any) => item.transactionType === 'expense')
          : [];

        data.expenses = {
          total: expenseItems.reduce((sum: number, item: any) => sum + (item.amount || 0), 0),
          items: expenseItems.map((item: any) => ({
            chartNumber: item.chartNumber || '',
            name: item.name || '',
            assistName: item.assistName || '',
            assistTeam: item.assistTeam || '',
            cashierName: item.cashierName || '',
            amount: item.amount || 0,
            description: item.description || ''
          }))
        };

        DEBUG && logger.info(`[하위 호환성] ${date}: financialTransactions에서 expenses 생성 - ${expenseItems.length}개 항목`);
      }

      return data;
    }
    return null;
  } catch (error) {
    DEBUG && logger.error('일일 보고서 로드 실패:', error);
    throw error;
  }
};

// 특정 날짜의 거래 데이터 가져오기
export const getFinancialTransactionData = async (date: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    const docSnapshot = await getDoc(dailyReportRef);

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();

      if (data.financialTransactions) {
        return data.financialTransactions;
      }

      if (data.expenses) {
        const convertedData = {
          totalExpense: data.expenses.total || 0,
          totalIncome: 0,
          netAmount: -(data.expenses.total || 0),
          items: (data.expenses.items || []).map((item: any) => ({
            ...item,
            transactionType: 'expense',
            category: '지출'
          }))
        };

        DEBUG && logger.info(`[하위 호환성] ${date}: expenses를 financialTransactions 형식으로 읽기 - ${convertedData.items.length}개 항목`);
        return convertedData;
      }
    }
    return null;
  } catch (error) {
    DEBUG && logger.error('거래 데이터 로드 실패:', error);
    throw error;
  }
};

// 날짜 범위를 기준으로 일일 보고서 데이터 가져오기
export const getDailyReportsByDateRange = async (startDate: string, endDate: string) => {
  try {
    const reportsCol = collection(db, 'dailyReports');
    // 문서 ID가 'YYYY-MM-DD'(날짜)이고 사전순=날짜순이므로 문서 ID 범위로 조회한다.
    // (updatedAt(마지막 수정 시각)로 필터하면 과거 보고서를 나중에 수정 시 범위에서 벗어나고,
    //  new Date(endDate)는 자정으로 파싱되어 endDate 하루가 통째로 누락됨)
    const q = query(reportsCol,
      where(documentId(), '>=', startDate),
      where(documentId(), '<=', endDate),
      orderBy(documentId())
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ date: doc.id, ...doc.data() }));
  } catch (error) {
    DEBUG && logger.error('일일 보고서 범위 조회 실패:', error);
    throw error;
  }
};

// 일일 보고서 항목 삭제하기
export const deleteDailyReportItem = async (date: string, itemType: string, itemId: string) => {
  try {
    const dailyReportRef = doc(db, 'dailyReports', date);
    // 해당 날짜 단일 문서만 읽는다. (컬렉션 전체 getDocs는 문서 수에 비례한 불필요한
    //  읽기 비용을 유발하고, list 권한이 제한된 보안 규칙에서는 삭제가 실패할 수 있음)
    const reportDoc = await getDoc(dailyReportRef);

    if (reportDoc.exists()) {
      const data = reportDoc.data();
      if (data[itemType] && Array.isArray(data[itemType])) {
        const updatedItems = data[itemType].filter((item: any) =>
          item.id !== itemId &&
          item.chartNumber !== itemId
        );

        await setDoc(dailyReportRef, {
          [itemType]: updatedItems,
          updatedAt: Timestamp.now()
        }, { merge: true });

        return true;
      }
    }
    return false;
  } catch (error) {
    DEBUG && logger.error('일일 보고서 항목 삭제 실패:', error);
    throw error;
  }
};
